import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Tabs, Select, message, Alert, Space, DatePicker, InputNumber } from 'antd';
import UserService from '@/services/user.service';
import CategoryService from '@/services/category.service';
import { logger } from '@/utils/logger';
import type { Participant } from '@/types/models';
import { apiService } from '@/services/api.service';
// import type { ApiResponse } from '@/types/api.types';
import { API_ENDPOINTS } from '@/constants/api.constants';
import type { Category } from '@/types/models/tournament';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { getDateValueFromEvent, datePickerProps } from '@/utils/datePickerUtils';

dayjs.locale('ru');

interface AddParticipantModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  clubId: number;
  clubName?: string;
}

const AddParticipantModal: React.FC<AddParticipantModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  clubId,
  clubName,
}) => {
  const [searchForm] = Form.useForm();
  const [createForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<Participant[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
  const [coaches, setCoaches] = useState<any[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingCoaches, setLoadingCoaches] = useState(false);

  // Функция для фильтрации категорий по возрасту, весу и полу
  const filterCategories = (birthDate: dayjs.Dayjs | null, weight: number | null, gender: string | null) => {
    if (!birthDate || !weight || !gender) {
      setFilteredCategories(allCategories);
      return;
    }

    // Рассчитываем возраст
    const today = dayjs();
    let calculatedAge = today.year() - birthDate.year();
    const monthDiff = today.month() - birthDate.month();
    if (monthDiff < 0 || (monthDiff === 0 && today.date() < birthDate.date())) {
      calculatedAge--;
    }

    // Фильтруем категории
    const filtered = allCategories.filter(category => {
      // Проверка возраста
      const ageMatch = calculatedAge >= category.ageFrom && calculatedAge <= category.ageTo;
      
      // Проверка веса
      const weightMatch = weight >= category.weightFrom && weight <= category.weightTo;
      
      // Проверка пола
      const genderMatch = category.gender === gender || category.gender === 'mixed';
      
      return ageMatch && weightMatch && genderMatch;
    });

    setFilteredCategories(filtered);    
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const categoriesData = await CategoryService.getAll();
        
        setAllCategories(categoriesData);
        setFilteredCategories(categoriesData);
      } catch (error) {
        logger.error('Error fetching categories:', error);
        message.error('Ошибка при загрузке категорий');
      } finally {
        setLoadingCategories(false);
      }
    };

    // Загрузка списка тренеров
    const fetchCoaches = async () => {
      try {
        setLoadingCoaches(true);

        // Загружаем только тренеров текущего клуба
        if (clubId) {          
          const response = await UserService.getClubCoaches(clubId);
          if (response.data && Array.isArray(response.data)) {
            // Добавляем полное имя для отображения
            const coachesWithFullName = response.data.map(coach => ({
              ...coach,
              fullName: `${coach.lastName || ''} ${coach.firstName || ''}${coach.user?.middleName ? ` ${coach.user.middleName}` : ''}`
            }));
            setCoaches(coachesWithFullName);            
          }
        } else {
          // Если clubId не передан, загружаем всех тренеров (старое поведение)          
          const { data } = await apiService.get<{ data: any[], status: number }>(API_ENDPOINTS.COACHES.BASE);
          if (data?.data) {
            setCoaches(data.data);            
          }
        }
      } catch (error) {
        logger.error('Error fetching coaches:', error);
        message.error('Ошибка при загрузке списка тренеров');
      } finally {
        setLoadingCoaches(false);
      }
    };

    if (visible) {
      fetchCategories();
      fetchCoaches();
    }
  }, [visible]);

  const handleSearch = async () => {
    try {
      setLoading(true);
      const values = await searchForm.validateFields();

      const response = await UserService.findParticipantsByName({
        firstName: values.firstName,
        lastName: values.lastName,
        middleName: values.middleName
      });

      // Фильтруем только неактивных участников
      const inactiveParticipants = response.data.filter(
        (participant: Participant) => participant.status === 'inactive'
      );

      setSearchResults(inactiveParticipants);

      if (inactiveParticipants.length === 0) {
        message.info('Неактивные участники не найдены');
      }
    } catch (error) {
      logger.error('Error searching participants:', error);
      message.error('Ошибка при поиске участников');
    } finally {
      setLoading(false);
    }
  };

  const handleAddExisting = async (participant: Participant) => {
    try {
      setLoading(true);
      await UserService.addParticipantToClub(participant.id, clubId);
      message.success('Участник успешно добавлен в клуб');
      setSearchResults([]);
      searchForm.resetFields();
      onSuccess();
    } catch (error) {
      logger.error('Error adding existing participant:', error);
      message.error('Ошибка при добавлении участника');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreParticipant = async (participant: Participant) => {
    try {
      setLoading(true);
      // Обновляем статус на active и добавляем в клуб
      await UserService.updateParticipant(participant.id, {
        status: 'active',
        clubId: clubId
      });
      message.success(`Спортсмен успешно возвращен в клуб${clubName ? ` "${clubName}"` : ''}`);
      setSearchResults([]);
      searchForm.resetFields();
      onSuccess();
    } catch (error) {
      logger.error('Error restoring participant:', error);
      message.error('Ошибка при возврате спортсмена');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      setLoading(true);
      const values = await createForm.validateFields();

      // Преобразуем дату рождения в формат YYYY-MM-DD
      // getDateValueFromEvent возвращает dayjs.Dayjs | null, поэтому проверяем и форматируем
      let birthDate: string | undefined = undefined;
      if (values.birthDate) {
        if (dayjs.isDayjs(values.birthDate) && values.birthDate.isValid()) {
          birthDate = values.birthDate.format('YYYY-MM-DD');
        } else {
          const parsedDate = dayjs(values.birthDate);
          if (parsedDate.isValid()) {
            birthDate = parsedDate.format('YYYY-MM-DD');
          }
        }
      }

      // Рассчитываем возраст на основе даты рождения (точный расчет с учетом месяца и дня)
      let age: number | undefined = undefined;
      if (values.birthDate) {
        const birthDateDayjs = dayjs.isDayjs(values.birthDate) && values.birthDate.isValid()
          ? values.birthDate
          : dayjs(values.birthDate).isValid()
            ? dayjs(values.birthDate)
            : null;
        if (birthDateDayjs) {
          const today = dayjs();
          let calculatedAge = today.year() - birthDateDayjs.year();
          const monthDiff = today.month() - birthDateDayjs.month();
          if (monthDiff < 0 || (monthDiff === 0 && today.date() < birthDateDayjs.date())) {
            calculatedAge--;
          }
          age = calculatedAge;
        }
      }

      await UserService.createParticipant({
        ...values,
        birthDate,
        age,
        beltLevel: values.beltLevel,
        belt: values.beltLevel, // Добавляем поле belt для бэкенда
        weight: parseFloat(values.weight),
        phoneNumber: values.phone || values.phoneNumber,
        clubId,
        role: 'participant',
        status: values.status || 'active'
      });

      message.success('Новый участник успешно создан');
      createForm.resetFields();
      onSuccess();
    } catch (error) {
      logger.error('Error creating participant:', error);
      message.error('Ошибка при создании участника');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    searchForm.resetFields();
    createForm.resetFields();
    setSearchResults([]);
    onCancel();
  };

  const items = [
    {
      key: 'search',
      label: 'Поиск',
      children: (
        <>
          <Alert
            message="Поиск неактивных спортсменов"
            description="Через поиск можно найти и вернуть неактивных спортсменов в клуб. Введите ФИО спортсмена для поиска."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Form form={searchForm} layout="vertical">
            <Form.Item
              name="lastName"
              label="Фамилия"
              rules={[{ required: true, message: 'Введите фамилию' }]}
            >
              <Input placeholder="Иванов" />
            </Form.Item>

            <Form.Item
              name="firstName"
              label="Имя"
              rules={[{ required: true, message: 'Введите имя' }]}
            >
              <Input placeholder="Иван" />
            </Form.Item>

            <Form.Item
              name="middleName"
              label="Отчество"
            >
              <Input placeholder="Иванович" />
            </Form.Item>

            <Form.Item>
              <Button type="primary" onClick={handleSearch} loading={loading}>
                Найти
              </Button>
            </Form.Item>
          </Form>

          {searchResults.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h4>Результаты поиска:</h4>
              {searchResults.map(participant => (
                <Alert
                  key={participant.id}
                  message={`${participant.lastName} ${participant.firstName} ${participant.middleName || ''}`}
                  description={
                    <>
                      <p>Email: {participant.email || participant.user?.email || 'Не указан'}</p>
                      {participant.beltLevel && <p>Пояс: {participant.beltLevel}</p>}
                      {clubName && <p><strong>Клуб:</strong> {clubName}</p>}
                      <Space style={{ marginTop: 8 }}>
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => handleRestoreParticipant(participant)}
                          loading={loading}
                        >
                          Вернуть
                        </Button>
                        <Button
                          size="small"
                          onClick={() => handleAddExisting(participant)}
                          loading={loading}
                        >
                          Добавить в клуб
                        </Button>
                      </Space>
                    </>
                  }
                  type="info"
                  style={{ marginBottom: 8 }}
                />
              ))}
            </div>
          )}
        </>
      ),
    },
    {
      key: 'create',
      label: 'Создать',
      children: (
        <Form 
          form={createForm} 
          layout="vertical"
          initialValues={{ gender: 'male', status: 'active' }}
        >
          <Form.Item
            name="lastName"
            label="Фамилия"
            rules={[{ required: true, message: 'Введите фамилию' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="firstName"
            label="Имя"
            rules={[{ required: true, message: 'Введите имя' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="middleName"
            label="Отчество"
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="gender"
            label="Пол"
            rules={[{ required: true, message: 'Выберите пол' }]}
          >
            <Select
              onChange={(value) => {
                const birthDate = createForm.getFieldValue('birthDate');
                const weight = createForm.getFieldValue('weight');
                filterCategories(birthDate, weight, value);
              }}
            >
              <Select.Option value="male">Мужской</Select.Option>
              <Select.Option value="female">Женский</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="birthDate"
            label="Дата рождения"
            rules={[{ required: true, message: 'Укажите дату рождения' }]}
            getValueFromEvent={getDateValueFromEvent}
          >
            <DatePicker 
              {...datePickerProps}
              placeholder="Выберите дату рождения (ДД.ММ.ГГГГ)"
              style={{ width: '100%' }}
              onChange={(date) => {
                const gender = createForm.getFieldValue('gender');
                const weight = createForm.getFieldValue('weight');
                filterCategories(date, weight, gender);
              }}
            />
          </Form.Item>

          <Form.Item
            name="weight"
            label="Вес (кг)"
            rules={[
              { required: true, message: 'Введите вес' },
              { type: 'number', min: 0.1, message: 'Вес должен быть положительным числом' }
            ]}
          >
            <InputNumber 
              min={0.1} 
              max={200} 
              step={0.1} 
              precision={1}
              style={{ width: '100%' }}
              onChange={(value) => {
                const birthDate = createForm.getFieldValue('birthDate');
                const gender = createForm.getFieldValue('gender');
                filterCategories(birthDate, value as number, gender);
              }}
            />
          </Form.Item>

          <Form.Item
            name="beltLevel"
            label="Уровень пояса"
            rules={[{ required: true, message: 'Выберите уровень пояса' }]}
          >
            <Select>
              <Select.Option value="11 кю">11 кю</Select.Option>
              <Select.Option value="10 кю">10 кю</Select.Option>
              <Select.Option value="9 кю">9 кю</Select.Option>
              <Select.Option value="8 кю">8 кю</Select.Option>
              <Select.Option value="7 кю">7 кю</Select.Option>
              <Select.Option value="6 кю">6 кю</Select.Option>
              <Select.Option value="5 кю">5 кю</Select.Option>
              <Select.Option value="4 кю">4 кю</Select.Option>
              <Select.Option value="3 кю">3 кю</Select.Option>
              <Select.Option value="2 кю">2 кю</Select.Option>
              <Select.Option value="1 кю">1 кю</Select.Option>
              <Select.Option value="1 дан">1 дан</Select.Option>
              <Select.Option value="2 дан">2 дан</Select.Option>
              <Select.Option value="3 дан">3 дан</Select.Option>
              <Select.Option value="4 дан">4 дан</Select.Option>
              <Select.Option value="5 дан">5 дан</Select.Option>
              <Select.Option value="6 дан">6 дан</Select.Option>
              <Select.Option value="7 дан">7 дан</Select.Option>
              <Select.Option value="8 дан">8 дан</Select.Option>
              <Select.Option value="9 дан">9 дан</Select.Option>
              <Select.Option value="10 дан">10 дан</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { type: 'email', message: 'Введите корректный email' }
            ]}
          >
            <Input type="email" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Телефон"
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="categoryId"
            label="Категория"
            rules={[{ required: true, message: 'Выберите категорию' }]}
            extra={filteredCategories.length === 0 && (createForm.getFieldValue('birthDate') || createForm.getFieldValue('weight') || createForm.getFieldValue('gender')) 
              ? 'Нет подходящих категорий для указанных параметров' 
              : filteredCategories.length < allCategories.length 
                ? `Показано ${filteredCategories.length} из ${allCategories.length} категорий (отфильтровано по возрасту, весу и полу)`
                : undefined}
          >
            <Select
              placeholder="Выберите категорию"
              loading={loadingCategories}
              showSearch
              optionFilterProp="children"
            >
              {filteredCategories.map((category) => (
                <Select.Option key={category.id} value={category.id}>
                  {category.name} ({category.ageFrom}-{category.ageTo} лет, {category.weightFrom}-{category.weightTo} кг, {category.gender === 'male' ? 'М' : category.gender === 'female' ? 'Ж' : 'М/Ж'})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="coachId"
            label="Тренер"
            rules={[{ required: false, message: 'Выберите тренера' }]}
          >
            <Select
              placeholder="Выберите тренера"
              loading={loadingCoaches}
              showSearch
              optionFilterProp="children"
              allowClear
            >
              {coaches.map((coach) => (
                <Select.Option key={coach.id} value={coach.id}>
                  {coach.fullName || (coach.user ? `${coach.user.firstName || ''} ${coach.user.lastName || ''}` : `Тренер ID: ${coach.id}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label="Статус"
            rules={[{ required: true, message: 'Выберите статус' }]}
          >
            <Select>
              <Select.Option value="active">Активен</Select.Option>
              <Select.Option value="inactive">Неактивен</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" onClick={handleCreate} loading={loading}>
              Создать
            </Button>
          </Form.Item>
        </Form>
      ),
    },
  ];

  return (
    <Modal
      title="Добавление спортсмена"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={700}
    >
      <Tabs defaultActiveKey="search" items={items} />
    </Modal>
  );
};

export default AddParticipantModal;